/**
 * Centralized localStorage key definitions.
 *
 * Keys are split into two categories:
 * - UI_KEYS: Visual preferences that are global to the browser
 * - SERVER_SCOPED_KEYS: Settings that need to be scoped by server installId
 *
 * When accessing via yepanywhere.com/remote, users may connect to different
 * servers. Server-scoped settings ensure each server has independent config.
 */
import { generateUUID } from "./uuid";

// ============================================================================
// Global Install ID (set once on connection, used for key scoping)
// ============================================================================

let currentInstallId: string | undefined;

/** Set the current server's install ID (called once on connection) */
export function setCurrentInstallId(installId: string): void {
  const prevInstallId = currentInstallId;
  currentInstallId = installId;

  // Run migration if this is a new install ID
  if (prevInstallId !== installId) {
    const migrated = migrateLegacySettings(installId);
    if (migrated) {
      console.log(
        "[storageKeys] Migrated legacy localStorage keys to scoped keys",
      );
    }
  }
}

/** Get the current server's install ID (undefined if not yet set) */
export function getCurrentInstallId(): string | undefined {
  return currentInstallId;
}

// ============================================================================
// UI Preferences (global to browser, not scoped by server)
// ============================================================================

export const UI_KEYS = {
  locale: "yep-anywhere-locale",
  theme: "yep-anywhere-theme",
  fontSize: "yep-anywhere-font-size",
  outputProseFont: "yep-anywhere-output-prose-font",
  outputUiFont: "yep-anywhere-output-ui-font",
  outputProseFontSize: "yep-anywhere-output-prose-font-size",
  outputFixedFont: "yep-anywhere-output-fixed-font",
  outputFixedFontSizeOffset: "yep-anywhere-output-fixed-font-size-offset",
  outputProseThinkingFontSizeOffset:
    "yep-anywhere-output-prose-thinking-font-size-offset",
  outputProseMathFontSizeOffset:
    "yep-anywhere-output-prose-math-font-size-offset",
  outputProseLineSpacingPercent:
    "yep-anywhere-output-prose-line-spacing-percent",
  outputProseVerticalSpacing: "yep-anywhere-output-prose-vertical-spacing",
  outputProseVerticalSpacingPercent:
    "yep-anywhere-output-prose-vertical-spacing-percent",
  outputToolPreviewLineCount: "yep-anywhere-output-tool-preview-line-count",
  tabSize: "yep-anywhere-tab-size",
  contentMaxWidth: "yep-anywhere-content-max-width",
  sidebarWidth: "yep-anywhere-sidebar-width",
  sidebarExpanded: "yep-anywhere-sidebar-expanded",
  sidebarSectionExpansion: "yep-anywhere-sidebar-section-expansion",
  funPhrases: "yep-anywhere-fun-phrases-enabled",
  streamingEnabled: "yep-anywhere-streaming-enabled",
  speechKeepMicWarm: "yep-anywhere-speech-keep-mic-warm",
  speechMicDeviceId: "yep-anywhere-speech-mic-device-id",
  floatingActionButtonEnabled: "yep-anywhere-floating-action-button-enabled",
  developerMode: "yep-anywhere-developer-mode",
  sessionToolbarVisibility: "yep-anywhere-session-toolbar-visibility",
  sessionHoverCardShowDelayMs: "yep-anywhere-session-hover-card-show-delay-ms",
  sessionHoverCardMaxHeightPx: "yep-anywhere-session-hover-card-max-height-px",
  sessionGeneratedTitleLength: "yep-anywhere-session-generated-title-length",
  alwaysShowQuoteCircles: "yep-anywhere-always-show-quote-circles",
  sessionThinkingVisible: "yep-anywhere-session-thinking-visible",
  sessionThinkingLatestOnly: "yep-anywhere-session-thinking-latest-only",
  stableToolPreviewRendering:
    "yep-anywhere-stable-tool-preview-rendering-enabled",
  // Preserve the first-shipped key; true now means previews start expanded.
  inlineMediaExpandedByDefault: "yep-anywhere-inline-images-enabled",
  schemaValidation: "yep-anywhere-schema-validation",
  emulatorMaxFps: "yep-anywhere-emulator-max-fps",
  emulatorMaxWidth: "yep-anywhere-emulator-max-width",
  emulatorQuality: "yep-anywhere-emulator-quality",
  emulatorAdaptiveFps: "yep-anywhere-emulator-adaptive-fps",
  attachmentUploadQuality: "yep-anywhere-attachment-upload-quality",
  tabTitleActivityEnabled: "yep-anywhere-tab-title-activity-enabled",
  settingsIconStyle: "yep-anywhere-settings-icon-style",
  // Legacy key from the first checkbox version of this client-local setting.
  flatSettingsIcons: "yep-anywhere-flat-settings-icons-enabled",
} as const;

// ============================================================================
// Server-Scoped Settings (prefixed with installId)
// ============================================================================

/** Base key names for server-scoped settings (installId prefix added at runtime) */
export const SERVER_SCOPED_KEYS = {
  model: "model",
  thinkingLevel: "thinking-level",
  thinkingEnabled: "thinking-enabled",
  thinkingMode: "thinking-mode",
  showThinking: "show-thinking",
  forkSummaryAutoOpen: "fork-summary-auto-open",
  voiceInputEnabled: "voice-input-enabled",
  speechMethod: "speech-method",
  speechSmartTurn: "speech-smart-turn",
  grokSpeechAudio: "grok-speech-audio",
  parakeetSpeechModel: "parakeet-speech-model",
  xaiSttApiKey: "xai-stt-api-key",
  browserProfileId: "browser-profile-id",
  notifyInApp: "notify-in-app",
  recentProject: "recent-project",
} as const;

/** Build a server-scoped storage key */
export function serverKey(
  installId: string,
  key: (typeof SERVER_SCOPED_KEYS)[keyof typeof SERVER_SCOPED_KEYS],
): string {
  return `yep-anywhere-${installId}-${key}`;
}

/**
 * Get a server-scoped value from localStorage.
 * Falls back to unscoped key if installId is not yet available.
 */
export function getServerScoped(
  key: keyof typeof SERVER_SCOPED_KEYS,
  legacyKey?: string,
): string | null {
  const installId = currentInstallId;
  if (installId) {
    const scopedKey = serverKey(installId, SERVER_SCOPED_KEYS[key]);
    return localStorage.getItem(scopedKey);
  }
  // Fallback to legacy key if available
  if (legacyKey) {
    return localStorage.getItem(legacyKey);
  }
  return null;
}

/**
 * Set a server-scoped value in localStorage.
 * Falls back to unscoped key if installId is not yet available.
 */
export function setServerScoped(
  key: keyof typeof SERVER_SCOPED_KEYS,
  value: string,
  legacyKey?: string,
): void {
  const installId = currentInstallId;
  if (installId) {
    const scopedKey = serverKey(installId, SERVER_SCOPED_KEYS[key]);
    localStorage.setItem(scopedKey, value);
  } else if (legacyKey) {
    // Fallback to legacy key
    localStorage.setItem(legacyKey, value);
  }
}

/**
 * Remove a server-scoped value from localStorage.
 */
export function removeServerScoped(
  key: keyof typeof SERVER_SCOPED_KEYS,
  legacyKey?: string,
): void {
  const installId = currentInstallId;
  if (installId) {
    const scopedKey = serverKey(installId, SERVER_SCOPED_KEYS[key]);
    localStorage.removeItem(scopedKey);
  }
  // Also remove legacy key if present
  if (legacyKey) {
    localStorage.removeItem(legacyKey);
  }
}

/**
 * Get or create the browser profile ID.
 * This identifies the browser profile (shared across tabs) for connection tracking.
 * Creates a new UUID if one doesn't exist.
 */
export function getOrCreateBrowserProfileId(): string {
  let browserProfileId = getServerScoped(
    "browserProfileId",
    LEGACY_KEYS.browserProfileId,
  );
  if (!browserProfileId) {
    browserProfileId = generateUUID();
    setServerScoped(
      "browserProfileId",
      browserProfileId,
      LEGACY_KEYS.browserProfileId,
    );
  }
  return browserProfileId;
}

// ============================================================================
// Dynamic Key Builders (for session/project-specific keys)
// ============================================================================

export const KEY_BUILDERS = {
  /** Draft message for an existing session */
  draftMessage: (installId: string, sessionId: string) =>
    `yep-anywhere-${installId}-draft-${sessionId}`,

  /** Draft for a new session in a project */
  newSessionDraft: (installId: string, projectId: string) =>
    `yep-anywhere-${installId}-new-session-draft-${projectId}`,

  /** FAB draft content */
  fabDraft: (installId: string) => `yep-anywhere-${installId}-fab-draft`,

  /** FAB prefill content */
  fabPrefill: (installId: string) => `yep-anywhere-${installId}-fab-prefill`,
} as const;

// ============================================================================
// Special Keys (not scoped, handle their own structure)
// ============================================================================

/** Remote connection credentials - stored per wsUrl internally */
export const REMOTE_CREDENTIALS_KEY = "yep-anywhere-remote-credentials";

/** Saved hosts for multi-host remote access */
export const SAVED_HOSTS_KEY = "yep-anywhere-saved-hosts";

// ============================================================================
// Legacy Key Mappings (for migration from old unscoped keys)
// ============================================================================

/** Old unscoped keys that need migration to server-scoped versions */
export const LEGACY_KEYS = {
  model: "yep-anywhere-model",
  thinkingLevel: "yep-anywhere-thinking-level",
  thinkingEnabled: "yep-anywhere-thinking-enabled",
  thinkingMode: "yep-anywhere-thinking-mode",
  showThinking: "yep-anywhere-show-thinking",
  voiceInputEnabled: "yep-anywhere-voice-input-enabled",
  speechMethod: "yep-anywhere-speech-method",
  speechSmartTurn: "yep-anywhere-speech-smart-turn",
  grokSpeechAudio: "yep-anywhere-grok-speech-audio",
  parakeetSpeechModel: "yep-anywhere-parakeet-speech-model",
  xaiSttApiKey: "yep-anywhere-xai-stt-api-key",
  browserProfileId: "yep-anywhere-device-id",
  notifyInApp: "yep-anywhere-notify-in-app",
  recentProject: "yep-anywhere-recent-project",
  // Draft keys had different prefixes
  draftMessagePrefix: "draft-message-",
  newSessionDraftPrefix: "draft-new-session-",
  fabDraft: "fab-draft",
  fabPrefill: "fab-prefill",
} as const;

/** Keys that need renaming (old name -> new name in UI_KEYS) */
export const UI_KEY_RENAMES = {
  "sidebar-expanded": UI_KEYS.sidebarExpanded,
} as const;

// ============================================================================
// Migration Helper
// ============================================================================

/**
 * Migrate legacy unscoped settings to server-scoped keys.
 * Call this once when the installId becomes available.
 * Returns true if any migrations were performed.
 */
export function migrateLegacySettings(installId: string): boolean {
  let migrated = false;

  // Migrate server-scoped keys
  const scopedMigrations: Array<{
    legacy: string;
    scoped: keyof typeof SERVER_SCOPED_KEYS;
  }> = [
    { legacy: LEGACY_KEYS.model, scoped: "model" },
    { legacy: LEGACY_KEYS.thinkingLevel, scoped: "thinkingLevel" },
    { legacy: LEGACY_KEYS.thinkingEnabled, scoped: "thinkingEnabled" },
    { legacy: LEGACY_KEYS.showThinking, scoped: "showThinking" },
    { legacy: LEGACY_KEYS.voiceInputEnabled, scoped: "voiceInputEnabled" },
    { legacy: LEGACY_KEYS.speechMethod, scoped: "speechMethod" },
    { legacy: LEGACY_KEYS.speechSmartTurn, scoped: "speechSmartTurn" },
    {
      legacy: LEGACY_KEYS.parakeetSpeechModel,
      scoped: "parakeetSpeechModel",
    },
    { legacy: LEGACY_KEYS.xaiSttApiKey, scoped: "xaiSttApiKey" },
    { legacy: LEGACY_KEYS.browserProfileId, scoped: "browserProfileId" },
    { legacy: LEGACY_KEYS.notifyInApp, scoped: "notifyInApp" },
    { legacy: LEGACY_KEYS.recentProject, scoped: "recentProject" },
  ];

  for (const { legacy, scoped } of scopedMigrations) {
    const value = localStorage.getItem(legacy);
    if (value !== null) {
      const newKey = serverKey(installId, SERVER_SCOPED_KEYS[scoped]);
      // Only migrate if new key doesn't already exist
      if (localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, value);
        migrated = true;
      }
      // Remove legacy key after migration
      localStorage.removeItem(legacy);
    }
  }

  // Migrate UI key renames
  for (const [oldKey, newKey] of Object.entries(UI_KEY_RENAMES)) {
    const value = localStorage.getItem(oldKey);
    if (value !== null) {
      if (localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, value);
        migrated = true;
      }
      localStorage.removeItem(oldKey);
    }
  }

  // Migrate FAB keys
  const fabMigrations: Array<{
    legacy: string;
    builder: "fabDraft" | "fabPrefill";
  }> = [
    { legacy: LEGACY_KEYS.fabDraft, builder: "fabDraft" },
    { legacy: LEGACY_KEYS.fabPrefill, builder: "fabPrefill" },
  ];

  for (const { legacy, builder } of fabMigrations) {
    const value = localStorage.getItem(legacy);
    if (value !== null) {
      const newKey = KEY_BUILDERS[builder](installId);
      if (localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, value);
        migrated = true;
      }
      localStorage.removeItem(legacy);
    }
  }

  // Migrate draft keys (draft-message-* and draft-new-session-*)
  const allKeys = Object.keys(localStorage);
  for (const key of allKeys) {
    if (key.startsWith(LEGACY_KEYS.draftMessagePrefix)) {
      const sessionId = key.slice(LEGACY_KEYS.draftMessagePrefix.length);
      const value = localStorage.getItem(key);
      if (value !== null) {
        const newKey = KEY_BUILDERS.draftMessage(installId, sessionId);
        if (localStorage.getItem(newKey) === null) {
          localStorage.setItem(newKey, value);
          migrated = true;
        }
        localStorage.removeItem(key);
      }
    } else if (key.startsWith(LEGACY_KEYS.newSessionDraftPrefix)) {
      const projectId = key.slice(LEGACY_KEYS.newSessionDraftPrefix.length);
      const value = localStorage.getItem(key);
      if (value !== null) {
        const newKey = KEY_BUILDERS.newSessionDraft(installId, projectId);
        if (localStorage.getItem(newKey) === null) {
          localStorage.setItem(newKey, value);
          migrated = true;
        }
        localStorage.removeItem(key);
      }
    }
  }

  return migrated;
}
